import React, { useState, useEffect } from 'react';
import { useSocialTrading } from '../contexts/SocialTradingContext';

const SocialTradingModal = ({ isOpen, onClose, userDisplayName }) => {
  const { 
    activeCompetitions, 
    userCompetitions, 
    userProfile, 
    userPortfolios,
    registerForCompetition, 
    createUserProfile,
    loading 
  } = useSocialTrading();

  const [activeTab, setActiveTab] = useState('competitions');
  const [registrationStatus, setRegistrationStatus] = useState({});

  const handleRegistration = async (competitionId) => {
    if (!userDisplayName) {
      alert('Please set your display name first');
      return;
    }

    // Create user profile if doesn't exist
    if (!userProfile) {
      const profileResult = await createUserProfile(userDisplayName);
      if (!profileResult.success) {
        alert(`Failed to create profile: ${profileResult.error}`);
        return;
      }
    }

    // Register for competition
    const result = await registerForCompetition(competitionId, userDisplayName);
    
    setRegistrationStatus({
      [competitionId]: result.success ? 'success' : 'failed'
    });

    if (result.success) {
      const competition = activeCompetitions.find(c => c.competition_id === competitionId);
      if (competition) {
        alert(`🎉 Successfully registered for ${competition.title}!\n\n` +
              `Starting Capital: $${competition.trading_rules.starting_capital.toLocaleString()}\n` +
              `Duration: ${competition.market_scenario.duration_hours} hours\n` +
              `Environment: ${competition.market_scenario.scenario_name}\n\n` +
              `🏆 Your trades will now be tracked in this tournament!\n` +
              `Start trading to see your performance on the leaderboard!`);
      }
    } else {
      alert(`Registration failed: ${result.error || 'Unknown error'}`);
    }
  };

  if (!isOpen) return null;

  return (
    // FIXED: Enhanced backdrop and modal styling for maximum visibility
    <div className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50 p-4 backdrop-blur-md">
      <div className="bg-atticus-navy-950 rounded-lg border border-yellow-400/30 w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl relative">
        {/* Enhanced overlay for extra background blocking */}
        <div className="absolute inset-0 bg-atticus-navy-950/98 rounded-lg backdrop-blur-sm"></div>
        
        {/* Content with higher z-index and solid backgrounds */}
        <div className="relative z-10">
          {/* Header with solid background */}
          <div className="flex items-center justify-between p-6 border-b border-yellow-400/20 bg-atticus-navy-950">
            <h2 className="text-2xl font-bold text-yellow-400">Social Trading & Tournaments</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl transition-colors"
            >
              ×
            </button>
          </div>

          {/* Tab Navigation with solid background */}
          <div className="flex border-b border-yellow-400/20 bg-atticus-navy-950">
            <button 
              onClick={() => setActiveTab('competitions')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'competitions' 
                  ? 'text-yellow-400 border-b-2 border-yellow-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              🏆 Competitions
            </button>
            <button 
              onClick={() => setActiveTab('leaderboard')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'leaderboard' 
                  ? 'text-yellow-400 border-b-2 border-yellow-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              📊 Leaderboard
            </button>
            <button 
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'profile' 
                  ? 'text-yellow-400 border-b-2 border-yellow-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              👤 Profile
            </button>
          </div>

          {/* Content with solid background */}
          <div className="p-6 overflow-y-auto max-h-[60vh] bg-atticus-navy-950">
            {activeTab === 'competitions' && (
              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mx-auto"></div>
                    <p className="text-gray-400 mt-2">Loading competitions...</p>
                  </div>
                ) : activeCompetitions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 mb-4 text-lg">No tournaments available</p>
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>• Check back later for new competitions</div>
                      <div>• Join the waitlist for upcoming tournaments</div>
                    </div>
                  </div>
                ) : (
                  activeCompetitions.map((competition) => (
                    <div key={competition.competition_id} className="bg-gradient-to-br from-atticus-navy-900 to-atticus-navy-800 rounded-xl p-4 md:p-6 border border-yellow-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-yellow-400/50">
                      {/* Header with title and status */}
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                        <div className="flex-1 mb-4 md:mb-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                            <h3 className="text-lg md:text-xl font-bold text-white">{competition.title}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold w-fit ${
                              competition.status === 'active' 
                                ? 'bg-gradient-to-r from-green-500/20 to-green-600/20 text-green-400 border border-green-400/30' 
                                : 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 text-yellow-400 border border-yellow-400/30'
                            }`}>
                              {competition.status.toUpperCase()}
                            </span>
                          </div>
                          
                          <p className="text-gray-300 mb-3 leading-relaxed text-sm md:text-base">{competition.description}</p>
                          
                          {competition.competition_id === 'beginner_options_academy' && (
                            <div className="mb-3">
                              <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-500/20 to-blue-500/20 text-green-400 rounded-full text-xs font-semibold border border-green-400/30">
                                🚀 INSTANT ACCESS - No requirements!
                              </span>
                            </div>
                          )}
                          
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-yellow-400">💰</span>
                              <span className="text-white font-medium">${competition.trading_rules.starting_capital.toLocaleString()}</span>
                              <span className="text-gray-500">capital</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-yellow-400">👥</span>
                              <span className="text-white font-medium">{competition.participants.length}</span>
                              <span className="text-gray-500">/{competition.entry_requirements.max_participants}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-yellow-400">⏱️</span>
                              <span className="text-white font-medium">{competition.market_scenario.duration_hours}h</span>
                              <span className="text-gray-500">duration</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Join button - centered on mobile, right-aligned on desktop */}
                        <div className="flex justify-center md:justify-end">
                          <button 
                            onClick={() => handleRegistration(competition.competition_id)}
                            disabled={loading || registrationStatus[competition.competition_id] === 'success'}
                            className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                              registrationStatus[competition.competition_id] === 'success'
                                ? 'bg-gradient-to-r from-green-500/20 to-green-600/20 text-green-400 border border-green-400/30 cursor-not-allowed'
                                : competition.competition_id === 'beginner_options_academy'
                                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-green-500/25'
                                : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-300 hover:to-yellow-400 shadow-lg hover:shadow-yellow-400/25'
                            }`}
                          >
                            {registrationStatus[competition.competition_id] === 'success' 
                              ? '✓ Registered' 
                              : competition.competition_id === 'beginner_options_academy'
                              ? '🚀 Join Now!'
                              : 'Join Competition'
                            }
                          </button>
                        </div>
                      </div>

                      {/* Progress indicator for registered users */}
                      {userPortfolios[competition.competition_id] && (
                        <div className="mt-4 p-3 md:p-4 bg-gradient-to-r from-green-500/10 to-green-600/10 border border-green-400/30 rounded-lg">
                          <h5 className="text-green-400 font-semibold mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                            🏆 Your Tournament Progress
                          </h5>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 text-xs md:text-sm">
                            <div className="text-center">
                              <div className="text-white font-bold text-base md:text-lg">
                                ${userPortfolios[competition.competition_id].current_balance.toLocaleString()}
                              </div>
                              <div className="text-gray-400 text-xs">Portfolio Value</div>
                            </div>
                            <div className="text-center">
                              <div className="text-white font-bold text-base md:text-lg">
                                {userPortfolios[competition.competition_id].total_trades}
                              </div>
                              <div className="text-gray-400 text-xs">Total Trades</div>
                            </div>
                            <div className="text-center">
                              <div className={`font-bold text-base md:text-lg ${userPortfolios[competition.competition_id].total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {userPortfolios[competition.competition_id].total_pnl >= 0 ? '+' : ''}${userPortfolios[competition.competition_id].total_pnl.toLocaleString()}
                              </div>
                              <div className="text-gray-400 text-xs">P&L</div>
                            </div>
                            <div className="text-center">
                              <div className="text-white font-bold text-base md:text-lg">
                                {(userPortfolios[competition.competition_id].win_rate || 0).toFixed(1)}%
                              </div>
                              <div className="text-gray-400 text-xs">Win Rate</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Competition Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mt-4">
                        <div className="bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg p-3 md:p-4 border border-atticus-navy-600">
                          <h4 className="text-yellow-400 font-semibold mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                            📋 Requirements
                          </h4>
                          {competition.competition_id === 'beginner_options_academy' ? (
                            <div className="space-y-1 md:space-y-2">
                              <p className="text-xs md:text-sm text-green-400 font-medium">✅ None - Join instantly!</p>
                              <p className="text-xs md:text-sm text-gray-300">Level: {competition.entry_requirements.min_skill_level}</p>
                              <p className="text-xs md:text-sm text-gray-300">Fee: Free</p>
                            </div>
                          ) : (
                            <div className="space-y-1 md:space-y-2">
                              <p className="text-xs md:text-sm text-gray-300">XP: {competition.entry_requirements.min_xp}</p>
                              <p className="text-xs md:text-sm text-gray-300">Level: {competition.entry_requirements.min_skill_level}</p>
                              <p className="text-xs md:text-sm text-gray-300">Fee: ${competition.entry_requirements.entry_fee_virtual}</p>
                            </div>
                          )}
                        </div>
                        <div className="bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg p-3 md:p-4 border border-atticus-navy-600">
                          <h4 className="text-yellow-400 font-semibold mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                            ⚖️ Trading Rules
                          </h4>
                          <div className="space-y-1 md:space-y-2">
                            <p className="text-xs md:text-sm text-gray-300">Max Position: ${competition.trading_rules.max_position_size.toLocaleString()}</p>
                            <p className="text-xs md:text-sm text-gray-300">Daily Loss Limit: ${competition.trading_rules.risk_limits.max_daily_loss.toLocaleString()}</p>
                            <p className="text-xs md:text-sm text-gray-300">Scoring: {competition.trading_rules.scoring_method.replace('_', ' ')}</p>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg p-3 md:p-4 border border-atticus-navy-600 sm:col-span-2 lg:col-span-1">
                          <h4 className="text-yellow-400 font-semibold mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                            🏆 Prizes
                          </h4>
                          <div className="space-y-1 md:space-y-2">
                            <p className="text-xs md:text-sm text-gray-300">1st: ${competition.prize_structure.first_place.virtual_currency.toLocaleString()}</p>
                            <p className="text-xs md:text-sm text-gray-300">XP: {competition.prize_structure.first_place.xp_reward}</p>
                            <p className="text-xs md:text-sm text-gray-300">Badges: {competition.prize_structure.first_place.badges.length}</p>
                          </div>
                        </div>
                      </div>

                      {competition.competition_id === 'beginner_options_academy' && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-blue-500/10 to-blue-600/10 border border-blue-400/30 rounded-lg">
                          <h5 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">🎓 Learn by Doing</h5>
                          <p className="text-sm text-gray-300 leading-relaxed">
                            This competition is designed for learning through experience. No prior knowledge required - 
                            jump in and discover options trading with guided scenarios and educational checkpoints!
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-atticus-navy-900 to-atticus-navy-800 rounded-xl p-6 border border-yellow-400/30 shadow-xl">
                  <h3 className="text-xl font-bold text-yellow-400 mb-6 flex items-center gap-2">🏆 Live Competition Rankings</h3>
                  
                  {Object.keys(userPortfolios).length > 0 ? (
                    <div className="space-y-4">
                      {Object.entries(userPortfolios).map(([competitionId, portfolio]) => {
                        const competition = activeCompetitions.find(c => c.competition_id === competitionId);
                        return (
                          <div key={competitionId} className="bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg p-4 border border-atticus-navy-600">
                            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">{competition?.title}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div className="text-center">
                                <div className="text-yellow-400 font-bold text-xl">#{1}</div>
                                <div className="text-gray-400 text-xs">Your Rank</div>
                              </div>
                              <div className="text-center">
                                <div className="text-white font-bold text-xl">${portfolio.current_balance.toLocaleString()}</div>
                                <div className="text-gray-400 text-xs">Portfolio Value</div>
                              </div>
                              <div className="text-center">
                                <div className={`font-bold text-xl ${portfolio.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {portfolio.total_pnl >= 0 ? '+' : ''}${portfolio.total_pnl.toLocaleString()}
                                </div>
                                <div className="text-gray-400 text-xs">P&L</div>
                              </div>
                              <div className="text-center">
                                <div className="text-white font-bold text-xl">{portfolio.total_trades}</div>
                                <div className="text-gray-400 text-xs">Total Trades</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-400 mb-4 text-lg">
                        Join a competition to see your performance on the leaderboard
                      </p>
                      <div className="text-sm text-gray-500 space-y-1">
                        <div>• Track your performance against other traders</div>
                        <div>• See live portfolio values and returns</div>
                        <div>• Monitor risk-adjusted performance metrics</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-atticus-navy-900 to-atticus-navy-800 rounded-xl p-6 border border-yellow-400/30 shadow-xl">
                  <h3 className="text-xl font-bold text-yellow-400 mb-6 flex items-center gap-2">👤 Your Trading Profile</h3>
                  
                  {Object.keys(userPortfolios).length > 0 ? (
                    <div>
                      {/* User Stats Overview */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <div className="text-center p-4 bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg border border-atticus-navy-600">
                          <div className="text-3xl font-bold text-white">{Object.keys(userPortfolios).length}</div>
                          <div className="text-sm text-gray-400">Competitions Joined</div>
                        </div>
                        <div className="text-center p-4 bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg border border-atticus-navy-600">
                          <div className="text-3xl font-bold text-green-400">
                            {Object.values(userPortfolios).reduce((max, p) => 
                              Math.max(max, ((p.current_balance - p.starting_balance) / p.starting_balance) * 100), -100
                            ).toFixed(1)}%
                          </div>
                          <div className="text-sm text-gray-400">Best Performance</div>
                        </div>
                        <div className="text-center p-4 bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg border border-atticus-navy-600">
                          <div className="text-3xl font-bold text-white">
                            {Object.values(userPortfolios).reduce((sum, p) => sum + p.total_trades, 0)}
                          </div>
                          <div className="text-sm text-gray-400">Total Trades</div>
                        </div>
                        <div className="text-center p-4 bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg border border-atticus-navy-600">
                          <div className="text-3xl font-bold text-yellow-400">
                            {Object.keys(userPortfolios).length * 100 + 50}
                          </div>
                          <div className="text-sm text-gray-400">XP Points</div>
                        </div>
                      </div>
                      
                      {/* Achievement Badges */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">🏅 Achievement Badges</h4>
                        <div className="flex flex-wrap gap-3">
                          <span className="px-4 py-2 bg-gradient-to-r from-green-500/20 to-green-600/20 text-green-400 rounded-full text-sm font-medium border border-green-400/30">
                            🎓 Academy Graduate
                          </span>
                          {Object.values(userPortfolios).some(p => p.total_pnl > 0) && (
                            <span className="px-4 py-2 bg-gradient-to-r from-blue-500/20 to-blue-600/20 text-blue-400 rounded-full text-sm font-medium border border-blue-400/30">
                              📈 First Profit
                            </span>
                          )}
                          {Object.values(userPortfolios).some(p => p.total_trades >= 5) && (
                            <span className="px-4 py-2 bg-gradient-to-r from-purple-500/20 to-purple-600/20 text-purple-400 rounded-full text-sm font-medium border border-purple-400/30">
                              🔥 Active Trader
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Competition History */}
                      <div>
                        <h4 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">📊 Competition History</h4>
                        <div className="space-y-3">
                          {Object.entries(userPortfolios).map(([competitionId, portfolio]) => {
                            const competition = activeCompetitions.find(c => c.competition_id === competitionId);
                            const returnPct = ((portfolio.current_balance - portfolio.starting_balance) / portfolio.starting_balance) * 100;
                            return (
                              <div key={competitionId} className="flex justify-between items-center p-4 bg-gradient-to-br from-atticus-navy-800 to-atticus-navy-700 rounded-lg border border-atticus-navy-600">
                                <span className="text-white font-medium">{competition?.title}</span>
                                <span className={`font-bold text-lg ${returnPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-400 mb-4 text-lg">
                        Join competitions to build your trading profile
                      </p>
                      <div className="text-sm text-gray-500 space-y-1">
                        <div>• XP and skill level progression</div>
                        <div>• Earned badges and achievements</div>
                        <div>• Competition history and statistics</div>
                        <div>• Platform privileges and recognition</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};



export default SocialTradingModal;
